/**
 * Shared text normalization for the search surfaces.
 *
 * Lowercase + strip diacritics (NFD) + collapse whitespace. This is the
 * comparison form used by the FAQ matcher, the biomarker/disease explorer,
 * and result highlighting — previously copy-pasted identically into each.
 * One definition keeps them from drifting apart (a divergence here silently
 * changes what matches).
 */
export function normalizeForSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
}

/** Split a query into normalized tokens (empty-safe). */
export function tokenizeForSearch(s: string): string[] {
  const norm = normalizeForSearch(s)
  return norm ? norm.split(" ").filter(Boolean) : []
}

/**
 * Filler words, in the locales the search surfaces render in (en/de/fr/nl).
 * Already diacritic-stripped, so they compare against `normalizeForSearch`
 * output directly.
 *
 * Used ONLY to decide which tokens a partial-match fallback should require.
 * Never used to rewrite a query, so exact matching and ranking are untouched.
 *
 * Why this exists: every search surface required *every* typed token to match,
 * so one filler word forced a zero result. In the 30 days to 2026-08-07 that
 * was the single largest mechanical cause of dead ends — 8 of the 10 FAQ dead
 * ends were question-shaped ("ich habe metallimplantate in der
 * lendenwirbelsäule" returned nothing while two FAQ entries discussed
 * implants), and `shbg and albumin` died on the word "and".
 */
export const SEARCH_STOPWORDS: ReadonlySet<string> = new Set([
  // en
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "can",
  "do",
  "does",
  "for",
  "from",
  "have",
  "how",
  "i",
  "in",
  "is",
  "it",
  "me",
  "my",
  "of",
  "on",
  "or",
  "the",
  "to",
  "was",
  "what",
  "when",
  "where",
  "which",
  "who",
  "why",
  "will",
  "with",
  "you",
  "your",
  // de (diacritics already stripped: "fur" = für)
  "auf",
  "aus",
  "bei",
  "das",
  "dem",
  "den",
  "der",
  "des",
  "die",
  "ein",
  "eine",
  "einen",
  "es",
  "fur",
  "haben",
  "hat",
  "ich",
  "im",
  "ist",
  "kann",
  "mein",
  "meine",
  "mit",
  "nach",
  "sich",
  "sie",
  "und",
  "von",
  "wann",
  "warum",
  "wie",
  "wo",
  "zu",
  "zum",
  "zur",
  // fr
  "au",
  "aux",
  "avec",
  "ce",
  "dans",
  "de",
  "des",
  "du",
  "est",
  "et",
  "il",
  "je",
  "la",
  "le",
  "les",
  "ma",
  "mon",
  "ou",
  "par",
  "pour",
  "que",
  "quel",
  "quelle",
  "qui",
  "sur",
  "un",
  "une",
  // nl
  "een",
  "en",
  "het",
  "hoe",
  "ik",
  "kan",
  "mijn",
  "met",
  "van",
  "voor",
  "wat",
  "waar",
])

/** Minimum token length that can discriminate. Below this a token matches
 *  far too much to be worth falling back on. */
export const CONTENT_TOKEN_MIN_LENGTH = 3

/** The tokens of a query that actually carry meaning — long enough to
 *  discriminate and not filler. Input must already be normalized. */
export function contentTokens(tokens: readonly string[]): string[] {
  return tokens.filter(
    (t) => t.length >= CONTENT_TOKEN_MIN_LENGTH && !SEARCH_STOPWORDS.has(t)
  )
}
